import React, { useRef, useState } from "react";
import SmartImage from "./SmartImage";
import { useAllowsHeavyMedia, useIsNearViewport } from "../../hooks";
import "./LiveCover.css";

/**
 * A project's cover photograph, which comes to life when it is looked at.
 *
 * The still is always rendered and is never replaced: the loop fades in on
 * top of it once it can actually play, and fades out again if anything goes
 * wrong. So the worst case — no loop, slow connection, decoding failure,
 * reduced-motion — is exactly the page as it was before, with no gap, no
 * spinner and no layout shift.
 *
 * Three gates decide whether the loop is downloaded at all, and they matter
 * because the home page carries seven of these:
 *
 *   `preview`         projects without one simply render the still.
 *   heavy media       `useAllowsHeavyMedia` already refuses on reduced-motion,
 *                     Save-Data and slow connections. A looping background is
 *                     exactly what those settings exist to suppress.
 *   near the viewport the <video> is not mounted until its card approaches,
 *                     and is unmounted when it leaves. Nothing off-screen is
 *                     ever fetched, and no more than a couple of video
 *                     decoders are alive at once.
 *
 * The <video> carries no `poster`. There is no point fetching a second copy
 * of a picture that is already painted directly underneath it.
 *
 * @param {Object} props
 * @param {string} props.coverSrc Still shown always, and while the loop loads.
 * @param {string} props.alt      Alt text for the still. "" if decorative.
 * @param {import("../../data/projects").ProjectPreview} [props.preview]
 * @param {string} [props.sizes]
 * @param {"lazy"|"eager"} [props.loading]
 * @param {boolean} [props.priority]
 * @returns {JSX.Element}
 */
function LiveCover({
  coverSrc,
  alt,
  preview,
  sizes,
  loading = "lazy",
  priority = false,
}) {
  // Observed instead of a wrapper element, so the still and the loop stay
  // siblings inside `.frame` and keep its object-fit and hover-zoom rules.
  const stillRef = useRef(null);

  const allowsHeavyMedia = useAllowsHeavyMedia();
  const isNearViewport = useIsNearViewport(stillRef);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const shouldMountFilm =
    Boolean(preview) && allowsHeavyMedia && isNearViewport && !hasFailed;

  return (
    <>
      <SmartImage
        ref={stillRef}
        src={coverSrc}
        alt={alt}
        sizes={sizes}
        loading={loading}
        priority={priority}
      />

      {shouldMountFilm && (
        <video
          className="live-cover__film"
          /* Only revealed once the browser says it can play, so the still is
             never swapped for a black or half-decoded frame. */
          data-playing={isPlaying ? "true" : "false"}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          tabIndex={-1}
          aria-hidden="true"
          onPlaying={() => setIsPlaying(true)}
          onError={() => setHasFailed(true)}
        >
          {preview.sources.map((previewSource) => (
            <source
              key={previewSource.src}
              src={previewSource.src}
              type={previewSource.type}
            />
          ))}
        </video>
      )}
    </>
  );
}

export default LiveCover;
